/**
 * Handoff Message
 *
 * "Continue in Chat" posts this into the new room so whoever answers there
 * (the chosen agent, or Neuro) picks up the assistant conversation instead
 * of starting cold. Plain text the room renders as markdown: the latest
 * turns that fit, tool calls reduced to one line each.
 */
import type { ChatMessage } from '@neuro/chat-adapter';

export const HANDOFF_MAX_CHARS = 8000;
const PER_MESSAGE_MAX_CHARS = 1500;

function clip(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

function messageLine(message: ChatMessage): string | null {
  if (message.role === 'user' && message.content?.trim()) {
    return `**You:** ${clip(message.content, PER_MESSAGE_MAX_CHARS)}`;
  }
  if (message.role === 'assistant' && message.toolCall) {
    return `_(the assistant used ${message.toolCall.name})_`;
  }
  if (message.role === 'assistant' && message.content?.trim()) {
    return `**Assistant:** ${clip(message.content, PER_MESSAGE_MAX_CHARS)}`;
  }
  return null;
}

/**
 * The message to post, or null when there's no conversation to carry over.
 * `pageLabel` names the page the conversation happened on.
 */
export function buildHandoffMessage(
  messages: readonly ChatMessage[],
  pageLabel?: string,
  maxChars = HANDOFF_MAX_CHARS,
): string | null {
  const lines = messages.map(messageLine).filter((line): line is string => !!line);
  if (!lines.some((line) => line.startsWith('**You:**'))) return null;

  const header = pageLabel
    ? `Continuing from the Assistant panel (on ${pageLabel}). The conversation so far:`
    : 'Continuing from the Assistant panel. The conversation so far:';
  const kept: string[] = [];
  let size = header.length;
  for (let i = lines.length - 1; i >= 0; i--) {
    const next = lines[i].length + 2;
    if (kept.length > 0 && size + next > maxChars) break;
    kept.unshift(lines[i]);
    size += next;
  }
  const omitted = kept.length < lines.length ? ['_(earlier messages left out)_'] : [];
  return [header, ...omitted, ...kept].join('\n\n');
}
